import * as React from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  apiClient,
  ApiError,
  DEFAULT_SHIRT_OPTOUT_CENTS,
  formatEventDates,
  hasProgramFinished,
  isEnrolmentClosed,
  isProgramArchived,
  looksReadyToArchive,
  ProgramInput,
  type Program,
  type ProgramInput as ProgramInputT,
  type ProgramLateInvite,
} from "@alight/contracts";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@alight/ui";
import { Archive, ArchiveRestore, Check, Copy, Pencil, Plus, Trash2 } from "lucide-react";
import { useEnrolmentUiConfig } from "../config";
import type { Brand } from "../brand";
import { lateRegistrationPath, programParam, registrationPath } from "../program-url";

/** Known registration forms (Forms page tabs). "Other…" reveals a free-text
 *  input for a future custom form key. */
const FORM_KEY_OPTIONS = [
  { value: "enrolment", label: "Enrolment (term classes — asks year level)" },
  { value: "holiday", label: "Holiday program (asks date of birth + medical)" },
  { value: "camp", label: "Camp (tickets — asks date of birth + medical)" },
  { value: "camp_leader", label: "Camp leaders (the leader registers themselves)" },
  { value: "leader", label: "Leaders & volunteers (the person registers themselves)" },
] as const;
const FORM_KEY_OTHER = "__other__";

/** Sentinel for "no co-branding" — a Radix Select item cannot have an empty
 *  string value, so the stored null/"" is represented by this in the dropdown. */
const ALIGHT_BRAND_VALUE = "__alight__";

/** Same trick for "no fee waiver" — a Radix Select item can't hold "". */
const NO_WAIVER_VALUE = "__none__";

/** Weekday picker options — value is the JS Date.getUTCDay() index we store. */
const WEEKDAYS = [
  { value: "0", label: "Sundays" },
  { value: "1", label: "Mondays" },
  { value: "2", label: "Tuesdays" },
  { value: "3", label: "Wednesdays" },
  { value: "4", label: "Thursdays" },
  { value: "5", label: "Fridays" },
  { value: "6", label: "Saturdays" },
] as const;

/** Vite proxies `/api` to the Worker in dev; relative base is correct. */
const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

/**
 * THE LINK THE HOST HANDS OUT — what they paste into a text message, a
 * bulletin or a Facebook post.
 *
 * It is the SHARE link (`/api/share/<program>`), not the registration page
 * directly, and the difference is the picture. WhatsApp, Facebook and iMessage
 * draw their preview card from the raw HTML they fetch — they never run the
 * site's JavaScript and they ignore the `?program=` part entirely, so every
 * program link previewed with the same site logo and the same generic blurb.
 * The share link is answered by the Worker, which knows which program was
 * asked for and hands back that program's own picture, title and details
 * before forwarding the person on to the registration form (handlers/share.ts).
 *
 * Absolute, because a relative path is useless outside the browser it was
 * copied from.
 *
 * Null for an INFO-ONLY listing (enrollable = 0): there is no registration to
 * link to, and handing out a link that opens someone else's form is worse than
 * having no button. Everything else gets one, including a hidden or finished
 * program — the host is often preparing next term's link before turning it on,
 * and the Status column beside it already says which state it is in.
 */
function shareableLink(program: Program, all: readonly Program[]): string | null {
  if (program.enrollable !== 1) return null;
  const origin = typeof window === "undefined" ? "" : window.location.origin;
  return `${origin}/api/share/${encodeURIComponent(programParam(program, all))}`;
}

/**
 * Where that link lands — shown under the box so the host can see it opens the
 * right form. Kept separate from the link itself: this is the destination, not
 * the thing to copy.
 */
function registrationLink(
  program: Program,
  all: readonly Program[],
  cfg: { registerPath: string; brands?: Record<string, Brand> },
): string {
  const origin = typeof window === "undefined" ? "" : window.location.origin;
  return `${origin}${registrationPath(program, all, cfg)}`;
}

/**
 * Copy-to-clipboard, with the fallback that makes it trustworthy.
 *
 * The clipboard API is refused outright in some browsers and in an embedded
 * preview frame, and a Copy button that silently does nothing is worse than no
 * button — the host pastes the last thing they copied into a message to a
 * parent. So a refusal REVEALS the link, selected and ready for Ctrl+C, and
 * says what happened.
 */
function CopyLinkButton({ url, label }: { url: string; label: string }) {
  const [state, setState] = React.useState<"idle" | "copied" | "manual">("idle");
  const inputRef = React.useRef<HTMLInputElement>(null);

  // The tick is feedback, not a mode — put it back so the button can be used
  // again without wondering whether the second click registered.
  React.useEffect(() => {
    if (state !== "copied") return;
    const timer = setTimeout(() => setState("idle"), 2000);
    return () => clearTimeout(timer);
  }, [state]);

  React.useEffect(() => {
    if (state === "manual") inputRef.current?.select();
  }, [state]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setState("copied");
    } catch {
      setState("manual");
    }
  }

  return (
    <div className="space-y-1.5">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => void copy()}
        title={url}
        aria-label={`Copy the registration link for ${label}`}
      >
        {state === "copied" ? (
          <Check className="h-4 w-4" aria-hidden />
        ) : (
          <Copy className="h-4 w-4" aria-hidden />
        )}
        {state === "copied" ? "Copied" : "Copy link"}
      </Button>
      {state === "manual" ? (
        <div className="space-y-1">
          <Input
            ref={inputRef}
            readOnly
            value={url}
            onFocus={(e) => e.currentTarget.select()}
            className="h-8 text-xs"
            aria-label={`Registration link for ${label}`}
          />
          <p className="text-xs text-muted">
            Your browser wouldn&apos;t let us copy it — press Ctrl+C (⌘C on a Mac).
          </p>
        </div>
      ) : null}
    </div>
  );
}

function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * LATE REGISTRATION LINKS — the one sanctioned way past a cut-off.
 *
 * Once a program's "Enrolments close after" date passes, the public Enrol Now
 * button disappears and the server refuses a late submission. That is the
 * point of the date. But a family who rings up and asks still has to be let in
 * somehow, and the only tools the host had were re-opening the date for
 * EVERYONE (and quietly collecting whoever else wandered in) or typing the
 * registration in by hand.
 *
 * So: a link carrying a long random code, which waives the cut-off. It used to
 * be ONE reusable link per program, on the grounds that the host wanted
 * something they could hand out repeatedly. The first time it mattered it was
 * forwarded to somebody else and two registrations arrived four days after the
 * holiday club closed — a link with no owner is a link anyone can use.
 *
 * So now it is ONE LINK PER FAMILY, tied to their email address and good for 48
 * hours. Forwarding it achieves nothing: the code is only half the key, and the
 * other half is being signed in as the person it was issued to.
 *
 * Every button here acts IMMEDIATELY, unlike the rest of this dialog, which
 * waits for Save — so it says so, in those words. A button that silently did
 * nothing until Save is a link the host texts to a parent before it exists.
 */
function LateLinkPanel({
  program,
  allPrograms,
  cfg,
}: {
  program: Program;
  allPrograms: readonly Program[];
  cfg: { registerPath: string; brands?: Record<string, Brand> };
}) {
  const api = React.useMemo(() => apiClient(API_BASE), []);
  const qc = useQueryClient();
  const [email, setEmail] = React.useState("");
  const [note, setNote] = React.useState("");

  const invitesQuery = useQuery({
    queryKey: ["admin", "programs", program.id, "late-invites"],
    queryFn: () => api.adminProgramLateInvites(program.id),
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["admin", "programs", program.id, "late-invites"] });

  const create = useMutation({
    mutationFn: () =>
      api.adminCreateProgramLateInvite(program.id, {
        email: email.trim(),
        note: note.trim() || undefined,
      }),
    onSuccess: () => {
      setEmail("");
      setNote("");
      void invalidate();
    },
  });
  const revoke = useMutation({
    mutationFn: (inviteId: number) => api.adminRevokeProgramLateInvite(program.id, inviteId),
    onSuccess: () => void invalidate(),
  });

  // An info-only listing has no form to get into, so there is nothing to let
  // anyone past — the same reason it has no registration link above.
  if (program.enrollable !== 1) return null;

  const invites = invitesQuery.data?.invites ?? [];
  const busy = create.isPending || revoke.isPending;
  const failed = create.error ?? revoke.error;
  const closesOn = program.enrol_cutoff_date
    ? formatEventDates(program.enrol_cutoff_date, null)
    : "the closing date";

  return (
    <div className="space-y-3 rounded-card border border-border bg-paper p-4">
      <Label>Late registration links</Label>
      <p className="text-xs text-muted">
        Once registrations close, this is how you let in a family who asks. Each link is made
        for <strong>one email address</strong> and stops working after <strong>48 hours</strong>
        . If it gets forwarded to somebody else it does them no good — they have to sign in as
        the person you made it for. Everyone else still sees “registrations are closed”, and
        registrations that come in this way are marked <strong>Late rego</strong> in your list.
      </p>

      <div className="space-y-2 rounded-card border border-border bg-card p-3">
        <div className="space-y-1">
          <Label htmlFor={`late-email-${program.id}`} className="text-xs">
            The family&apos;s email address
          </Label>
          <Input
            id={`late-email-${program.id}`}
            type="email"
            autoComplete="off"
            placeholder="parent@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-9"
          />
          <p className="text-xs text-muted">
            It must be the address they register with — the same one their sign-in link goes to.
          </p>
        </div>
        <div className="space-y-1">
          <Label htmlFor={`late-note-${program.id}`} className="text-xs">
            Note to yourself (optional)
          </Label>
          <Input
            id={`late-note-${program.id}`}
            placeholder="e.g. Karitori family — rang Tuesday"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="h-9"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy || email.trim() === ""}
          onClick={() => create.mutate()}
        >
          <Plus className="h-4 w-4" aria-hidden />
          {create.isPending ? "Making it…" : "Make a link for this family"}
        </Button>
        <p className="text-xs text-muted">
          It works straight away — you don&apos;t need to press Save. It gets them past{" "}
          {closesOn}, and nothing else: it can&apos;t get anyone into a program that has already{" "}
          <strong>finished</strong>.
        </p>
      </div>

      {invitesQuery.isPending ? (
        <p className="text-xs text-muted">Checking…</p>
      ) : invites.length === 0 ? (
        <p className="text-xs text-muted">You haven&apos;t made any links for this program.</p>
      ) : (
        <ul className="space-y-2">
          {invites.map((invite) => (
            <LateInviteRow
              key={invite.id}
              invite={invite}
              program={program}
              allPrograms={allPrograms}
              cfg={cfg}
              busy={busy}
              onRevoke={() => revoke.mutate(invite.id)}
            />
          ))}
        </ul>
      )}

      {failed ? (
        <p className="text-xs font-semibold text-error">
          {failed instanceof ApiError ? failed.message : "That didn't work — please try again."}
        </p>
      ) : null}
    </div>
  );
}

/** One issued link: who it was for, whether it still works, and the two things
 *  the host actually does with it — copy it, or turn it off. */
function LateInviteRow({
  invite,
  program,
  allPrograms,
  cfg,
  busy,
  onRevoke,
}: {
  invite: ProgramLateInvite;
  program: Program;
  allPrograms: readonly Program[];
  cfg: { registerPath: string; brands?: Record<string, Brand> };
  busy: boolean;
  onRevoke: () => void;
}) {
  /* Expiry is judged against the browser's clock only to decide what this row
     LOOKS like. The server judges it again on every use, so a laptop with the
     wrong date can make this row misleading but can never make a dead link
     work. */
  const expired = new Date(invite.expires_at).getTime() <= Date.now();
  const live = invite.revoked_at === null && !expired;
  const origin = typeof window === "undefined" ? "" : window.location.origin;
  /* Built by `lateRegistrationPath`, not glued together here: this link is
     `registrationPath` PLUS the code, and the gluing is what broke it — see
     that function for what a second "?" did to the families who followed it. */
  const url = `${origin}${lateRegistrationPath(program, allPrograms, cfg, invite.code)}`;

  return (
    <li className="space-y-1.5 rounded-card border border-border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-ink">{invite.email}</span>
        {invite.revoked_at !== null ? (
          <Badge variant="muted">Turned off</Badge>
        ) : expired ? (
          <Badge variant="muted">Expired</Badge>
        ) : (
          <Badge variant="accent">Works until {formatExpiry(invite.expires_at)}</Badge>
        )}
        {invite.used_count > 0 ? (
          <Badge variant="muted">
            Used for {invite.used_count} {invite.used_count === 1 ? "child" : "children"}
          </Badge>
        ) : null}
      </div>
      {invite.note ? <p className="text-xs text-muted">{invite.note}</p> : null}
      {live ? (
        <>
          <p className="break-all text-xs text-ink">{url}</p>
          <div className="flex flex-wrap items-center gap-2">
            <CopyLinkButton url={url} label={`late registration for ${invite.email}`} />
            <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={onRevoke}>
              Turn this one off
            </Button>
          </div>
        </>
      ) : (
        <p className="text-xs text-muted">
          This link no longer works. Make a new one above if they still need to register.
        </p>
      )}
    </li>
  );
}

/** "Fri 19 Sep, 4:12pm" — a 48-hour window is a time of day, not just a date,
 *  so the host can tell a parent on the phone how long they have. */
function formatExpiry(iso: string): string {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return "soon";
  return when
    .toLocaleString("en-AU", {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
    })
    .replace(/\s?([ap])m/i, (_m, p: string) => p.toLowerCase() + "m");
}

/** What archiving actually does, in the words the host needs to read BEFORE
 *  pressing it. Written once because it is both the tooltip and the thing that
 *  stops this looking like a delete. */
const ARCHIVE_NOTE =
  "Archive — hides this program's registrations from Submissions and takes it off the public site. Nothing is deleted, and you can bring it back at any time.";
const RESTORE_NOTE =
  "Restore — puts this program back on the public site and brings its registrations back into Submissions.";

/**
 * ARCHIVE / RESTORE one program — the tidy-up for a term that is over.
 *
 * It is reversible and deletes nothing, so unlike the bin beside it this one
 * acts on the first click with no confirmation to get through: making the host
 * confirm a harmless, undoable action is how they learn to click through the
 * confirmation that matters.
 *
 * The Submissions inbox is invalidated alongside the programs list because
 * archiving is precisely what decides which registrations that inbox shows — a
 * host who archives a finished holiday club and then looks at Submissions
 * would otherwise still see every one of its registrations waiting on them.
 *
 * Shaped as an icon button rather than an entry in a RowActions menu (the way
 * the Children table does it) because this table has never had a menu: Edit and
 * Delete sit here as icon buttons, and a menu holding this one action alone
 * would read as the only place to look for the other two. The plain-language
 * note a menu would show as a description is the tooltip instead.
 */
function ArchiveProgramAction({
  api,
  program,
  disabled,
}: {
  api: ReturnType<typeof apiClient>;
  program: Program;
  disabled: boolean;
}) {
  const qc = useQueryClient();
  const archived = isProgramArchived(program);
  const toggle = useMutation({
    mutationFn: () => api.adminArchiveProgram(program.id, !archived),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "programs"] });
      qc.invalidateQueries({ queryKey: ["admin", "enrolments"] });
    },
  });

  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8"
      aria-label={
        archived ? `Restore ${program.title}` : `Archive ${program.title}`
      }
      title={archived ? RESTORE_NOTE : ARCHIVE_NOTE}
      disabled={disabled || toggle.isPending}
      onClick={() => toggle.mutate()}
    >
      {archived ? (
        <ArchiveRestore className="h-4 w-4" />
      ) : (
        <Archive className="h-4 w-4" />
      )}
    </Button>
  );
}

export function AdminPrograms() {
  const api = React.useMemo(() => apiClient(API_BASE), []);
  const qc = useQueryClient();
  // Which page a program's families register on differs per site and per
  // program (co-branded programs go to the partner's page) — see
  // `registrationPath`.
  const { registerPath, brands } = useEnrolmentUiConfig();
  const [editing, setEditing] = React.useState<Program | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [showArchived, setShowArchived] = React.useState(false);

  const programsQuery = useQuery({
    queryKey: ["admin", "programs"],
    queryFn: () => api.adminListPrograms(),
  });

  const invalidate = React.useCallback(
    () => qc.invalidateQueries({ queryKey: ["admin", "programs"] }),
    [qc],
  );

  const upsert = useMutation({
    mutationFn: ({ input, id }: { input: ProgramInputT; id?: number }) =>
      api.adminUpsertProgram(input, id),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.adminDeleteProgram(id),
    onSuccess: invalidate,
  });

  const programs = programsQuery.data ?? [];
  const busy = upsert.isPending || remove.isPending;

  /* Archived programs are out of the way by default. This screen is the list of
     what the host is RUNNING, and a host who has filed away three years of
     terms shouldn't scroll past all of them to reach next term. They are never
     gone, only folded up behind the toggle in the header.

     `programs` stays the FULL list everywhere else on purpose: the share links
     are built from every program (`programParam` needs them all to tell two
     similarly named ones apart) and the fee-waiver dropdown must still be able
     to name an archived program. Only the rows are filtered. */
  const archivedCount = programs.filter((p) => isProgramArchived(p)).length;
  const rows = showArchived ? programs : programs.filter((p) => !isProgramArchived(p));

  /* Programs that LOOK finished and haven't been filed away yet. Counted here
     and said once above the table rather than badged on every row: the host
     reads this as a single chore to get to when they have a minute, not as
     something wrong with each program. Nothing is archived for them. */
  const readyToArchive = programs.filter((p) => looksReadyToArchive(p)).length;

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-heading text-xl font-bold text-ink">Programs</h2>
          <p className="mt-1 text-sm text-muted">
            Manage the programs shown on the public site — term classes and holiday programs
            alike. Add a new one, edit details, or hide a program by turning it inactive — no code
            required. <strong>Copy link</strong> gives you that program&apos;s own link, ready to
            paste into a message, a bulletin or a post — it shows the program&apos;s picture and
            details in the preview, and opens the form with that program already chosen.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {archivedCount > 0 ? (
            <Button
              type="button"
              variant={showArchived ? "default" : "outline"}
              size="sm"
              onClick={() => setShowArchived((v) => !v)}
              aria-pressed={showArchived}
            >
              {showArchived ? "Hide archived" : `Show archived (${archivedCount})`}
            </Button>
          ) : null}
          <Button onClick={() => setCreating(true)} disabled={programsQuery.isLoading}>
            <Plus className="h-4 w-4" />
            Add program
          </Button>
        </div>
      </header>

      {programsQuery.isLoading ? (
        <p className="text-sm text-muted">Loading programs…</p>
      ) : programsQuery.isError ? (
        <Card>
          <CardContent className="py-6">
            <p className="text-sm text-ink">
              Couldn’t load programs.{" "}
              {programsQuery.error instanceof ApiError
                ? programsQuery.error.message
                : "Please retry."}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => programsQuery.refetch()}
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {/* The gentle nudge — sits with the table rather than in the header
              because it is about the rows underneath it, and it disappears on
              its own once the host has tidied up. */}
          {readyToArchive > 0 ? (
            <p className="text-sm text-muted">
              {readyToArchive === 1
                ? "1 program looks finished — archive it to clear its registrations out of Submissions."
                : `${readyToArchive} programs look finished — archive them to clear their registrations out of Submissions.`}
            </p>
          ) : null}
          <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Term</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>When</TableHead>
                  <TableHead>Fee</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Registration link</TableHead>
                  {/* Three icon buttons now, so the column needs the room — at
                      w-32 the Archive button wrapped onto its own line. */}
                  <TableHead className="w-40 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-muted">
                      {/* An empty table means two different things, and "no
                          programs yet" would be a lie to a host who has simply
                          archived all of them. */}
                      {programs.length === 0
                        ? "No programs yet. Add the first one to show it on the site."
                        : "Every program is archived. Use “Show archived” above to see them."}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((p) => (
                    <TableRow
                      key={p.id}
                      className={isProgramArchived(p) ? "opacity-60" : undefined}
                    >
                      <TableCell className="text-sm text-muted">{p.term || "—"}</TableCell>
                      <TableCell className="font-medium text-ink">{p.title}</TableCell>
                      <TableCell className="text-sm text-muted">{p.schedule || "—"}</TableCell>
                      <TableCell className="text-sm text-ink">{dollars(p.amount_cents)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {/* ARCHIVED STANDS ALONE. Every other badge here
                              answers "where does this appear in public?", and
                              an archived program appears nowhere — so "Active"
                              or "Closes 3 Oct" beside "Archived" would describe
                              a program that is still running. The host filed
                              this one away as over; that is the whole status. */}
                          {isProgramArchived(p) ? (
                            <Badge variant="muted">Archived</Badge>
                          ) : (
                            <>
                              {p.active === 1 ? (
                                <Badge variant="accent">Active</Badge>
                              ) : (
                                <Badge variant="muted">Hidden</Badge>
                              )}
                              {p.enrollable === 0 && <Badge variant="muted">Info only</Badge>}
                              {/* Past its end date: still Active here, but off the
                                  public site. Say so, or "Active" would look wrong. */}
                              {hasProgramFinished(p) && <Badge variant="muted">Finished</Badge>}
                              {/* Only meaningful while the program is meant to take
                                  enrolments — an info-only card has no button to close. */}
                              {p.enrollable === 1 &&
                                !hasProgramFinished(p) &&
                                (isEnrolmentClosed(p) ? (
                                  <Badge variant="muted">Enrolments closed</Badge>
                                ) : p.enrol_cutoff_date ? (
                                  <Badge variant="muted">
                                    Closes {formatEventDates(p.enrol_cutoff_date, null)}
                                  </Badge>
                                ) : null)}
                            </>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const url = shareableLink(p, programs);
                          return url ? (
                            <CopyLinkButton url={url} label={p.title} />
                          ) : (
                            <span
                              className="text-sm text-muted"
                              title="An information-only listing has no registration form to link to. Tick “Open for enrolment” on the program to get a link."
                            >
                              —
                            </span>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            aria-label={`Edit ${p.title}`}
                            disabled={busy}
                            onClick={() => setEditing(p)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {/* Between Edit and Delete deliberately: it is the
                              middle ground between the two, and the host who
                              reaches for the bin to get a finished term out of
                              the way should meet the undoable option first. */}
                          <ArchiveProgramAction api={api} program={p} disabled={busy} />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            aria-label={`Delete ${p.title}`}
                            disabled={busy}
                            onClick={() => {
                              if (confirm(`Delete "${p.title}"? This cannot be undone.`)) {
                                remove.mutate(p.id);
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
          </Card>
        </div>
      )}

      <ProgramDialog
        key={editing ? `edit-${editing.id}` : creating ? "create" : "closed"}
        open={creating || editing !== null}
        program={editing}
        allPrograms={programs}
        pending={upsert.isPending}
        error={upsert.error instanceof ApiError ? upsert.error.message : null}
        nextSortOrder={
          // New programs go to the TOP of every list (newest term first):
          // lists sort ascending, so a new program takes the smallest number.
          programs.length > 0
            ? Math.min(...programs.map((p) => p.sort_order)) - 10
            : 10
        }
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSubmit={(input, id) =>
          upsert.mutateAsync({ input, id }).then(() => {
            setCreating(false);
            setEditing(null);
          })
        }
      />
    </section>
  );
}

interface ProgramDialogProps {
  open: boolean;
  program: Program | null;
  /** Every program, so the fee waiver can name one of the others. */
  allPrograms: Program[];
  pending: boolean;
  error: string | null;
  nextSortOrder: number;
  onClose: () => void;
  onSubmit: (input: ProgramInputT, id?: number) => Promise<unknown>;
}

/** One ticket row while it is being edited — prices as dollar strings. */
interface TicketDraft {
  key: string;
  label: string;
  standard: string;
  earlyBird: string;
  capacity: string;
  note: string;
  /** The `key` of another ticket that must sell out before this one opens — the
   *  reserve/overflow rule. "" = always available. */
  revealWhenSoldOut: string;
}

/** A URL-safe stable key derived from a ticket's label, for a brand-new row. */
function slugifyKey(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function ProgramDialog({
  open,
  program,
  allPrograms,
  pending,
  error,
  nextSortOrder,
  onClose,
  onSubmit,
}: ProgramDialogProps) {
  const isEdit = program !== null;
  const [title, setTitle] = React.useState(program?.title ?? "");
  const [term, setTerm] = React.useState(program?.term ?? "");
  const [schedule, setSchedule] = React.useState(program?.schedule ?? "");
  const [location, setLocation] = React.useState(program?.location ?? "");
  const [priceNote, setPriceNote] = React.useState(program?.price_note ?? "");
  const [amount, setAmount] = React.useState(((program?.amount_cents ?? 5000) / 100).toString());
  const [launchDate, setLaunchDate] = React.useState(program?.launch_date ?? "");
  const [active, setActive] = React.useState((program?.active ?? 1) === 1);
  const [enrollable, setEnrollable] = React.useState((program?.enrollable ?? 1) === 1);
  /* Link-only: live and registerable, but never advertised. Stored as
     `listed = 0`; the inverse of the checkbox, which asks the positive
     question ("show this on the website"). */
  const [listed, setListed] = React.useState((program?.listed ?? 1) === 1);
  /* Fee waiver: registering for THIS program is free for anyone already
     registered on the program chosen here — EXCEPT a leader who declares
     themselves under 18, who always pays (see the waiver in handlers/enrol.ts).
     "" = everybody pays. */
  const [freeIfEnrolledIn, setFreeIfEnrolledIn] = React.useState(
    program?.free_if_enrolled_program_id != null
      ? String(program.free_if_enrolled_program_id)
      : "",
  );
  const [lessonWeekday, setLessonWeekday] = React.useState(
    program?.lesson_weekday != null ? String(program.lesson_weekday) : "",
  );
  const [firstLessonDate, setFirstLessonDate] = React.useState(program?.first_lesson_date ?? "");
  const [startDate, setStartDate] = React.useState(program?.start_date ?? "");
  const [endDate, setEndDate] = React.useState(program?.end_date ?? "");
  const [imageUrl, setImageUrl] = React.useState(program?.image_url ?? "");
  const [cutoffDate, setCutoffDate] = React.useState(program?.enrol_cutoff_date ?? "");
  /* Who the program is for — free text ("Years 3 and up", "K-6"), shown as the
     Who row on the public card. "" = not stated, and the card shows no row. */
  const [ageRange, setAgeRange] = React.useState(program?.age_range ?? "");
  const initialFormKey = program?.form_key ?? "enrolment";
  const knownFormKey = FORM_KEY_OPTIONS.some((o) => o.value === initialFormKey);
  const [formKeyChoice, setFormKeyChoice] = React.useState(
    knownFormKey ? initialFormKey : FORM_KEY_OTHER,
  );
  const [customFormKey, setCustomFormKey] = React.useState(knownFormKey ? "" : initialFormKey);
  const [refPrefix, setRefPrefix] = React.useState(program?.ref_prefix ?? "ALC");
  const [notesHtml, setNotesHtml] = React.useState(program?.notes_html ?? "");
  /* Which branded registration page this program uses. "" = the ordinary
     Alight Creative journey at /enrol. */
  const [brandKey, setBrandKey] = React.useState(program?.brand_key ?? "");
  /* Co-branding options for THIS site — empty on a site that is itself the
     organisation running the program (see config.tsx). */
  const { brands, registerPath } = useEnrolmentUiConfig();
  /* FAMILY DISCOUNT — the PER-CHILD rate (in dollars) for the second child,
     the third, and so on. The first child's rate is always the fee above, so
     it is never edited here and the two can't drift apart. The LAST rate you
     enter also applies to every later child, so a short list prices any family
     size. Empty list = no discount. */
  const [familyTiers, setFamilyTiers] = React.useState<string[]>(
    (program?.family_pricing ?? []).slice(1).map((cents) => (cents / 100).toString()),
  );
  /* PART-DAY BOOKING — the days this program runs, and what 1, 2, 3 … of them
     cost ONE child. Empty list = the program is booked as a whole and no day
     question is asked, which is every ordinary program. */
  const [dayLabels, setDayLabels] = React.useState<string[]>(program?.days ?? []);
  const [dayRates, setDayRates] = React.useState<string[]>(
    (program?.day_pricing ?? []).map((cents) => (cents / 100).toString()),
  );
  /* TICKET TYPES — a camp/event where each registrant picks ONE priced ticket.
     Each row carries its stable key (kept from the stored ticket so a label or
     price can change without re-pricing past registrations; blank for a new row,
     derived from the label at save). Prices are in dollars for editing. Empty
     list = no ticket question, which is every ordinary program. */
  const [tickets, setTickets] = React.useState<TicketDraft[]>(
    (program?.ticket_types ?? []).map((t) => ({
      key: t.key,
      label: t.label,
      standard: (t.amount_cents / 100).toString(),
      earlyBird: t.early_bird_cents != null ? (t.early_bird_cents / 100).toString() : "",
      capacity: t.capacity != null ? String(t.capacity) : "",
      note: t.note ?? "",
      revealWhenSoldOut: t.reveal_when_sold_out ?? "",
    })),
  );
  /* The early-bird cut-off (ISO date). On/before it a ticket's early-bird price
     is charged; after it, the standard price. Blank = no early-bird window. */
  const [earlyBirdUntil, setEarlyBirdUntil] = React.useState(program?.early_bird_until ?? "");
  /* THE SHIRT OPT-OUT — what comes off when a family says their child doesn't
     need a program shirt. Stored on the program's own settings blob rather
     than in a column of its own, because it is one number that only a few
     programs ever set. Blank = the standard amount; 0 = don't offer it. */
  const [shirtOptOut, setShirtOptOut] = React.useState<string>(() => {
    const stored = program?.extra?.shirt_optout_cents;
    return typeof stored === "number" && Number.isFinite(stored) && stored >= 0
      ? (stored / 100).toString()
      : "";
  });
  const [localError, setLocalError] = React.useState<string | null>(null);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalError(null);

    const amountCents = Math.round(Number(amount) * 100);
    if (!Number.isFinite(amountCents) || amountCents < 0) {
      setLocalError("Enter a valid fee amount.");
      return;
    }

    const start = startDate.trim() === "" ? null : startDate.trim();
    const end = endDate.trim() === "" ? null : endDate.trim();
    if (start && end && end < start) {
      setLocalError("The end date can’t be before the start date.");
      return;
    }
    /* An end date on its own is legitimate: a weekly term has no "event start"
       to show on the card, but still needs a last day so it retires itself. */

    const cutoff = cutoffDate.trim() === "" ? null : cutoffDate.trim();
    // A cut-off after the event has finished can't close anything — almost
    // certainly a typo, so stop it rather than quietly saving a dead date.
    if (cutoff && end && cutoff > end) {
      setLocalError("The enrolment cut-off is after the program ends — check the date.");
      return;
    }

    const resolvedFormKey =
      formKeyChoice === FORM_KEY_OTHER ? customFormKey.trim() : formKeyChoice;
    if (resolvedFormKey === "") {
      setLocalError("Enter the registration form key (e.g. enrolment or holiday).");
      return;
    }

    /* Per-child rates, with the first child's taken from the fee above so the
       two can never disagree. A rate may not be GREATER than the one before —
       a discount that gets more expensive per child isn't a discount — and $0
       is allowed as an explicit "free from here" cap. */
    let familyPricing: number[] | null = null;
    if (familyTiers.length > 0) {
      const tiers = [amountCents];
      for (const [i, raw] of familyTiers.entries()) {
        const cents = Math.round(Number(raw) * 100);
        if (raw.trim() === "" || !Number.isFinite(cents) || cents < 0) {
          setLocalError(`Enter a rate for child ${i + 2}, or remove that row.`);
          return;
        }
        if (cents > (tiers[tiers.length - 1] ?? 0)) {
          setLocalError(
            `Child ${i + 2} costs more than child ${i + 1} — a sibling rate can stay the same or drop, but shouldn't rise.`,
          );
          return;
        }
        tiers.push(cents);
      }
      familyPricing = tiers;
    }

    /* The days themselves. Blank rows are dropped rather than rejected — an
       empty box is a row the host started and abandoned, not an error. */
    const days = dayLabels.map((d) => d.trim()).filter((d) => d !== "");
    /* One price per day COUNT: what a child pays for 1 day, for 2, and so on.
       Unlike the sibling table these may legitimately RISE (more days cost
       more), so the only rule is that they are real, non-negative amounts and
       that there is one for every day the program runs. */
    let dayPricing: number[] | null = null;
    if (days.length > 0 && dayRates.length > 0) {
      const rates: number[] = [];
      for (const [i, raw] of dayRates.entries()) {
        const cents = Math.round(Number(raw) * 100);
        if (raw.trim() === "" || !Number.isFinite(cents) || cents < 0) {
          setLocalError(`Enter a price for ${i + 1} day${i === 0 ? "" : "s"}, or remove that row.`);
          return;
        }
        rates.push(cents);
      }
      dayPricing = rates;
    }
    if (days.length > 0 && familyPricing) {
      setLocalError(
        "A program can be priced by days OR by a family discount, not both — the day prices would be ignored. Remove the family discount, or remove the days.",
      );
      return;
    }

    /* TICKET TYPES → a validated ticket_types array (or null for none). Prices
       convert dollars → cents; a blank early-bird price means the ticket always
       charges standard; a blank capacity means unlimited. Keys are kept stable
       from the stored ticket, derived from the label for a new row, and made
       unique so two tickets can never collide. */
    let ticketTypes: ProgramInputT["ticket_types"] = null;
    const filledTickets = tickets.filter((t) => t.label.trim() !== "");
    if (filledTickets.length > 0) {
      const seenKeys = new Set<string>();
      // Pass 1: validate and assign a final unique key per ticket, remembering
      // each draft's "base key" (what a reserve link points at) so Pass 2 can
      // resolve the links to final keys.
      const rows: Array<{
        draft: TicketDraft;
        finalKey: string;
        baseKey: string;
        std: number;
        eb: number | null;
        cap: number | null;
      }> = [];
      for (const t of filledTickets) {
        const std = Math.round(Number(t.standard) * 100);
        if (t.standard.trim() === "" || !Number.isFinite(std) || std < 0) {
          setLocalError(`Enter a valid standard price for the "${t.label.trim()}" ticket.`);
          return;
        }
        let eb: number | null = null;
        if (t.earlyBird.trim() !== "") {
          eb = Math.round(Number(t.earlyBird) * 100);
          if (!Number.isFinite(eb) || eb < 0) {
            setLocalError(`Enter a valid early-bird price for "${t.label.trim()}", or leave it blank.`);
            return;
          }
        }
        let cap: number | null = null;
        if (t.capacity.trim() !== "") {
          cap = Math.round(Number(t.capacity));
          if (!Number.isInteger(cap) || cap < 0) {
            setLocalError(`Enter a whole number of places for "${t.label.trim()}", or leave it blank for unlimited.`);
            return;
          }
        }
        const baseKey = (t.key.trim() || slugifyKey(t.label)).toLowerCase() || `ticket-${rows.length + 1}`;
        let key = baseKey;
        let n = 2;
        while (seenKeys.has(key)) key = `${baseKey}-${n++}`;
        seenKeys.add(key);
        rows.push({ draft: t, finalKey: key, baseKey, std, eb, cap });
      }
      // Pass 2: build the tickets, resolving each reserve link to the final key.
      const baseToFinal = new Map(rows.map((r) => [r.baseKey, r.finalKey]));
      ticketTypes = rows.map((r) => ({
        key: r.finalKey,
        label: r.draft.label.trim(),
        amount_cents: r.std,
        early_bird_cents: r.eb,
        capacity: r.cap,
        note: r.draft.note.trim(),
        reveal_when_sold_out: r.draft.revealWhenSoldOut
          ? (baseToFinal.get(r.draft.revealWhenSoldOut) ?? null)
          : null,
        sold_out: false,
        hidden: false,
      }));
    }
    const earlyBirdUntilClean = earlyBirdUntil.trim() === "" ? null : earlyBirdUntil.trim();
    if (ticketTypes && (days.length > 0 || familyPricing)) {
      setLocalError(
        "A program is priced by tickets OR by days/a family discount, not both — the ticket is the price. Remove the ticket types, or remove the days/family discount.",
      );
      return;
    }

    /* The shirt opt-out, kept on the program's settings blob. Left blank it is
       simply not stored, so the program keeps whatever the standard amount is
       rather than freezing today's number onto every program that is saved. */
    const programExtra: Record<string, unknown> = { ...(program?.extra ?? {}) };
    const shirtRaw = shirtOptOut.trim();
    if (shirtRaw === "") {
      delete programExtra.shirt_optout_cents;
    } else {
      const shirtCents = Math.round(Number(shirtRaw) * 100);
      if (!Number.isFinite(shirtCents) || shirtCents < 0) {
        setLocalError("Enter a valid shirt discount, or leave it blank for the standard one.");
        return;
      }
      // A discount bigger than the fee would make the place free, which is
      // never what a typo in this box is trying to say.
      if (shirtCents > amountCents) {
        setLocalError("The shirt discount is more than the program fee — check the amount.");
        return;
      }
      programExtra.shirt_optout_cents = shirtCents;
    }

    const candidate: ProgramInputT = {
      title: title.trim(),
      term: term.trim(),
      schedule: schedule.trim(),
      location: location.trim(),
      price_note: priceNote.trim(),
      amount_cents: amountCents,
      launch_date: launchDate.trim(),
      active: active ? 1 : 0,
      enrollable: enrollable ? 1 : 0,
      sort_order: program?.sort_order ?? nextSortOrder,
      lesson_weekday: lessonWeekday === "" ? null : Number(lessonWeekday),
      first_lesson_date: firstLessonDate.trim() === "" ? null : firstLessonDate.trim(),
      start_date: start,
      end_date: end,
      image_url: imageUrl.trim() === "" ? null : imageUrl.trim(),
      enrol_cutoff_date: cutoff,
      age_range: ageRange.trim() === "" ? null : ageRange.trim(),
      form_key: resolvedFormKey,
      ref_prefix: refPrefix.trim().toUpperCase(),
      notes_html: notesHtml,
      family_pricing: familyPricing,
      days: days.length > 0 ? days : null,
      day_pricing: dayPricing,
      ticket_types: ticketTypes,
      early_bird_until: earlyBirdUntilClean,
      brand_key: brandKey === "" ? null : brandKey,
      listed: listed ? 1 : 0,
      free_if_enrolled_program_id: freeIfEnrolledIn === "" ? null : Number(freeIfEnrolledIn),
      extra: programExtra,
    };

    const parsed = ProgramInput.safeParse(candidate);
    if (!parsed.success) {
      setLocalError(parsed.error.issues[0]?.message ?? "Please check the program details.");
      return;
    }

    void onSubmit(parsed.data, program?.id);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent className="flex max-h-[90dvh] flex-col">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit program" : "Add program"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update how this program appears on the public site."
              : "Add a program for families to enrol in."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col gap-4">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
          <div className="space-y-1.5">
            <Label htmlFor="p-title">Title</Label>
            <Input
              id="p-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Term 2 2026 Creative Writing"
              required
              autoFocus
            />
          </div>

          {/* The link for THIS program, where the host is already looking at
              it. Built from the SAVED program, not the box above: renaming the
              title changes the link, and showing a link that only works after
              you press Save would be a link that doesn't work. */}
          {program ? (
            <div className="space-y-1.5 rounded-card border border-border bg-paper p-4">
              <Label>Registration link</Label>
              {(() => {
                const url = shareableLink(program, allPrograms);
                const goesTo = registrationLink(program, allPrograms, { registerPath, brands });
                return url ? (
                  <>
                    <p className="break-all text-xs text-ink">{url}</p>
                    <CopyLinkButton url={url} label={program.title} />
                    <p className="text-xs text-muted">
                      Send this to a family and the form opens with this program already chosen
                      (it goes to <span className="break-all">{goesTo}</span>). It only works
                      while the program is <strong>Active</strong>, and{" "}
                      <strong>renaming the program changes the link</strong> — so re-copy it after
                      a rename.
                    </p>
                    <p className="text-xs text-muted">
                      {program.image_url ? (
                        <>
                          Pasted into a message, a post or a group chat, it previews with{" "}
                          <strong>this program&apos;s picture</strong> and details. Change the
                          picture below and new shares pick it up — apps remember the old preview
                          for links they have already seen.
                        </>
                      ) : (
                        <>
                          This program has no picture, so a message preview shows just the title
                          and details. Add a <strong>Preview image</strong> below and it appears in
                          the preview too.
                        </>
                      )}
                    </p>
                  </>
                ) : (
                  <p className="text-xs text-muted">
                    This program is an information-only listing, so there is no registration form
                    to link to. Tick <strong>Open for enrolment</strong> below and a link appears
                    here.
                  </p>
                );
              })()}
            </div>
          ) : null}

          {/* …and the PRIVATE one, right under it: the two links are the same
              question ("who can get into this form"), and the host reaches for
              the second one the moment the first stops working. */}
          {program ? (
            <LateLinkPanel
              program={program}
              allPrograms={allPrograms}
              cfg={{ registerPath, brands }}
            />
          ) : null}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="p-term">Term</Label>
              <Input
                id="p-term"
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder="Term 2 2026"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-launch">Launch date</Label>
              <Input
                id="p-launch"
                value={launchDate}
                onChange={(e) => setLaunchDate(e.target.value)}
                placeholder="24 April 2026"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="p-schedule">When</Label>
            <Input
              id="p-schedule"
              value={schedule}
              onChange={(e) => setSchedule(e.target.value)}
              placeholder="Fridays 4pm – 6pm"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="p-start-date">Event start date</Label>
              <Input
                id="p-start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-end-date">Event end date</Label>
              <Input
                id="p-end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
          <p className="-mt-2 text-xs text-muted">
            The first and last day the program runs. For a holiday club or special program the card
            shows the range (e.g. “Wed 30 Sep – Fri 2 Oct 2026”); for a weekly term, set the end
            date to the last lesson. Use “When” above for the time of day (e.g. “9am – 3pm”).
          </p>
          <p className="-mt-1 text-xs text-muted">
            <strong>The end date retires the program by itself:</strong> once it passes, the card
            disappears from the public programs page and it can no longer be enrolled in — so a
            finished term doesn’t linger with a live “Enrol Now” button. Leave it blank and the
            program stays listed until you untick “Active”.
          </p>
          {endDate.trim() !== "" &&
            (hasProgramFinished({ end_date: endDate.trim() }) ? (
              <p className="-mt-1 text-xs font-semibold text-accent-fg">
                This date has already passed — saving it removes the program from the site straight
                away.
              </p>
            ) : (
              <p className="-mt-1 text-xs text-muted">
                Listed until the end of <strong>{formatEventDates(endDate.trim(), null)}</strong>.
              </p>
            ))}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="p-lesson-day">Lesson day</Label>
              <Select
                value={lessonWeekday === "" ? undefined : lessonWeekday}
                onValueChange={setLessonWeekday}
              >
                <SelectTrigger id="p-lesson-day">
                  <SelectValue placeholder="e.g. Fridays" />
                </SelectTrigger>
                <SelectContent>
                  {WEEKDAYS.map((d) => (
                    <SelectItem key={d.value} value={d.value}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-first-lesson">First lesson date</Label>
              <Input
                id="p-first-lesson"
                type="date"
                value={firstLessonDate}
                onChange={(e) => setFirstLessonDate(e.target.value)}
              />
            </div>
          </div>
          <p className="-mt-2 text-xs text-muted">
            Used for the Attendance screen: weekly lessons are created automatically from the first
            lesson date while the program is active.
          </p>

          <div className="space-y-1.5">
            <Label htmlFor="p-age-range">Who it’s for (optional)</Label>
            <Input
              id="p-age-range"
              value={ageRange}
              onChange={(e) => setAgeRange(e.target.value)}
              placeholder="Years 3 and up"
            />
            <p className="text-xs text-muted">
              Shown as the “Who” line on the public program card — write it however parents will
              understand it (“Years 3 and up”, “K–6”, “Ages 5–12”). Leave blank to hide the line.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="p-location">Where</Label>
            <Input
              id="p-location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="88 Cecil Avenue, Castle Hill"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="p-image">Preview image (optional)</Label>
            <Input
              id="p-image"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://assets.alightcreative.com.au/…"
            />
            <p className="text-xs text-muted">
              Shown at the top of this program&apos;s card on the public Programs page. Upload a photo
              on the <strong>Assets</strong> page, use its <strong>Copy link</strong> button, and
              paste the link here. Leave blank for no image.
            </p>
            {imageUrl.trim() !== "" ? (
              <img
                src={imageUrl.trim()}
                alt="Preview"
                className="mt-1 block w-full max-w-xs rounded-card border border-border"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
                onLoad={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "";
                }}
              />
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="p-price">Cost note (shown on card)</Label>
              <Input
                id="p-price"
                value={priceNote}
                onChange={(e) => setPriceNote(e.target.value)}
                placeholder="$50 for the Alight Textbook"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-amount">Fee amount (AUD)</Label>
              <Input
                id="p-amount"
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="50.00"
              />
            </div>
          </div>

          {/* Co-branding only exists on a site that runs programs FOR other
              organisations. On a site that IS the organisation (htbh), the
              config supplies no brands and the control is not rendered — there
              is nothing to choose between. */}
          {brands && Object.keys(brands).length > 0 && (
            <div className="space-y-1.5">
              <Label htmlFor="p-brand">Registration page branding</Label>
              <Select value={brandKey === "" ? ALIGHT_BRAND_VALUE : brandKey} onValueChange={(v) => setBrandKey(v === ALIGHT_BRAND_VALUE ? "" : v)}>
                <SelectTrigger id="p-brand">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALIGHT_BRAND_VALUE}>Alight Creative (the usual page)</SelectItem>
                  {Object.values(brands).map((b) => (
                    <SelectItem key={b.key} value={b.key}>
                      {b.adminLabel}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted">
                For a program run with a partner church or organisation. Choosing a partner moves this
                program&apos;s registrations onto their own branded page — their logo and colours, none
                of the Alight menu — and the &ldquo;Enrol Now&rdquo; button on the Programs page sends
                families there instead. Registrations still arrive here in the host portal exactly as
                they always do.
              </p>
              {brandKey !== "" && brands[brandKey] && (
                <p className="text-xs text-muted">
                  Registration page:{" "}
                  <strong className="text-ink">{brands[brandKey]!.enrolPath}</strong> — it is
                  deliberately not linked from the menu or listed in search results, so share the link
                  directly.
                </p>
              )}
            </div>
          )}

          {/* ---- family discount ------------------------------------------ */}
          <div className="space-y-2 rounded-card border border-border bg-paper p-4">
            <Label>Family discount (optional)</Label>
            <p className="text-xs text-muted">
              A cheaper rate for each additional child. Set what the 2nd child costs, the 3rd,
              and so on — <strong>the last rate you enter also applies to every child after
              it</strong>, so three rows price a family of any size. Enter $0 as the last rate
              if extra children should be free from that point. Leave empty to charge the full
              fee for every child.
            </p>

            {familyTiers.length === 0 ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setFamilyTiers([amount])}
              >
                Add a family discount
              </Button>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-3 text-sm">
                  <span className="w-40 text-muted">1 child</span>
                  <span className="font-semibold text-ink">
                    ${(Math.round(Number(amount) * 100) / 100).toFixed(2)}
                  </span>
                  <span className="text-xs text-muted">(the fee above)</span>
                </div>
                {familyTiers.map((tier, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="w-40 text-sm text-muted">
                      {i === familyTiers.length - 1
                        ? `Child ${i + 2} onward`
                        : `Child ${i + 2}`}
                    </span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      className="max-w-[9rem]"
                      value={tier}
                      aria-label={`Fee for child ${i + 2}`}
                      onChange={(e) =>
                        setFamilyTiers((prev) =>
                          prev.map((t, j) => (j === i ? e.target.value : t)),
                        )
                      }
                    />
                    {i === familyTiers.length - 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setFamilyTiers((prev) => prev.slice(0, -1))}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setFamilyTiers((prev) => [...prev, prev[prev.length - 1] ?? amount])
                  }
                >
                  Add another child
                </Button>
                <p className="text-xs text-muted">
                  Each box is what that child costs <em>on their own</em>, not the family total —
                  the family pays the boxes added together. The last row repeats for every further
                  child, so set it to 0 to make the rest of the family free.
                </p>
              </div>
            )}
          </div>

          {/* ---- part-day booking ----------------------------------------- */}
          <div className="space-y-2 rounded-card border border-border bg-paper p-4">
            <Label>Let families pick which days (optional)</Label>
            <p className="text-xs text-muted">
              For a program families can come to <em>part</em> of. List the days it runs, then
              what one child pays for 1 day, 2 days, and so on. The registration form asks each
              child which days they are coming and charges accordingly. Leave empty and the
              program is booked as a whole, at the fee above.
            </p>

            {dayLabels.length === 0 ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setDayLabels([""])}
              >
                Add days families can pick
              </Button>
            ) : (
              <div className="space-y-3">
                <div className="space-y-2">
                  {dayLabels.map((day, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="w-20 text-sm text-muted">Day {i + 1}</span>
                      <Input
                        className="max-w-[20rem]"
                        value={day}
                        placeholder="e.g. Wednesday 30 September"
                        aria-label={`Label for day ${i + 1}`}
                        onChange={(e) =>
                          setDayLabels((prev) =>
                            prev.map((d, j) => (j === i ? e.target.value : d)),
                          )
                        }
                      />
                      {i === dayLabels.length - 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setDayLabels((prev) => prev.slice(0, -1));
                            /* The price list is indexed by day COUNT, so it can
                               never be longer than the number of days. */
                            setDayRates((prev) => prev.slice(0, Math.max(0, dayLabels.length - 1)));
                          }}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setDayLabels((prev) => [...prev, ""])}
                  >
                    Add another day
                  </Button>
                </div>

                <div className="space-y-2 border-t border-border pt-3">
                  <Label className="text-sm">What the days cost, per child</Label>
                  {dayLabels.map((_, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="w-20 text-sm text-muted">
                        {i + 1} {i === 0 ? "day" : "days"}
                      </span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        className="max-w-[9rem]"
                        value={dayRates[i] ?? ""}
                        aria-label={`Price for ${i + 1} ${i === 0 ? "day" : "days"}`}
                        onChange={(e) =>
                          setDayRates((prev) => {
                            const next = [...prev];
                            while (next.length <= i) next.push("");
                            next[i] = e.target.value;
                            return next.slice(0, dayLabels.length);
                          })
                        }
                      />
                    </div>
                  ))}
                  <p className="text-xs text-muted">
                    Leave these empty and every combination of days costs the full fee — useful
                    when you want to know who is coming when but the price doesn’t change. A
                    program priced by days can’t also have a family discount: the two would both
                    be setting the same fee.
                  </p>
                </div>

                {/* ---- the shirt opt-out ------------------------------------
                    Lives with the day prices because it only makes sense
                    alongside them: it exists for the family coming to one day
                    of three who would rather not pay for a shirt. */}
                <div className="space-y-2 border-t border-border pt-3">
                  <Label className="text-sm" htmlFor="p-shirt-optout">
                    Taken off if they don’t want a shirt
                  </Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted">$</span>
                    <Input
                      id="p-shirt-optout"
                      type="number"
                      min="0"
                      step="0.01"
                      className="max-w-[9rem]"
                      placeholder={(DEFAULT_SHIRT_OPTOUT_CENTS / 100).toString()}
                      value={shirtOptOut}
                      onChange={(e) => setShirtOptOut(e.target.value)}
                    />
                  </div>
                  <p className="text-xs text-muted">
                    The form offers each child “they don’t need a program shirt”, takes this off
                    their fee and stops asking for a shirt size. Leave it blank for the standard $
                    {(DEFAULT_SHIRT_OPTOUT_CENTS / 100).toString()}, or enter 0 to not offer it at
                    all. It only appears when the registration form actually asks for a shirt
                    size.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* ---- ticket types --------------------------------------------- */}
          <div className="space-y-2 rounded-card border border-border bg-paper p-4">
            <Label>Sell different tickets (optional)</Label>
            <p className="text-xs text-muted">
              For a camp or event where each person chooses one ticket, each at its own price
              (e.g. <em>Tenting</em> vs <em>Lodging</em>). The registration form asks each child
              which ticket and charges accordingly. Give a ticket a limited number of places and it
              shows as “Full” once they are taken. Leave empty for an ordinary single-price program.
            </p>

            {tickets.length === 0 ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setTickets([
                    { key: "", label: "", standard: "", earlyBird: "", capacity: "", note: "", revealWhenSoldOut: "" },
                  ])
                }
              >
                Add ticket types
              </Button>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Label className="text-sm" htmlFor="p-early-bird-until">
                    Early-bird prices apply until
                  </Label>
                  <Input
                    id="p-early-bird-until"
                    type="date"
                    className="max-w-[12rem]"
                    value={earlyBirdUntil}
                    onChange={(e) => setEarlyBirdUntil(e.target.value)}
                  />
                  <span className="text-xs text-muted">
                    Leave blank to charge the standard price all the way through.
                  </span>
                </div>

                {tickets.map((t, i) => (
                  <div key={i} className="space-y-2 rounded-card border border-border p-3">
                    <div className="flex items-center gap-3">
                      <Input
                        className="max-w-[16rem]"
                        value={t.label}
                        placeholder="Ticket name, e.g. Lodging"
                        aria-label={`Ticket ${i + 1} name`}
                        onChange={(e) =>
                          setTickets((prev) =>
                            prev.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)),
                          )
                        }
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setTickets((prev) => prev.filter((_, j) => j !== i))}
                      >
                        Remove
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-4">
                      <label className="text-xs text-muted">
                        <span className="mb-1 block">Standard price</span>
                        <span className="flex items-center gap-1">
                          <span>$</span>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            className="max-w-[8rem]"
                            value={t.standard}
                            aria-label={`Standard price for ticket ${i + 1}`}
                            onChange={(e) =>
                              setTickets((prev) =>
                                prev.map((x, j) => (j === i ? { ...x, standard: e.target.value } : x)),
                              )
                            }
                          />
                        </span>
                      </label>
                      <label className="text-xs text-muted">
                        <span className="mb-1 block">Early-bird price (optional)</span>
                        <span className="flex items-center gap-1">
                          <span>$</span>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            className="max-w-[8rem]"
                            value={t.earlyBird}
                            aria-label={`Early-bird price for ticket ${i + 1}`}
                            onChange={(e) =>
                              setTickets((prev) =>
                                prev.map((x, j) => (j === i ? { ...x, earlyBird: e.target.value } : x)),
                              )
                            }
                          />
                        </span>
                      </label>
                      <label className="text-xs text-muted">
                        <span className="mb-1 block">Places (optional)</span>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          className="max-w-[8rem]"
                          value={t.capacity}
                          placeholder="Unlimited"
                          aria-label={`Places for ticket ${i + 1}`}
                          onChange={(e) =>
                            setTickets((prev) =>
                              prev.map((x, j) => (j === i ? { ...x, capacity: e.target.value } : x)),
                            )
                          }
                        />
                      </label>
                    </div>
                    <Input
                      value={t.note}
                      placeholder="Short note shown under the ticket (optional)"
                      aria-label={`Note for ticket ${i + 1}`}
                      onChange={(e) =>
                        setTickets((prev) =>
                          prev.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)),
                        )
                      }
                    />
                    {/* Reserve / overflow link — this ticket opens automatically,
                        at its own (lower) price, only once the chosen ticket is
                        full. Only offered when there is another capped ticket to
                        gate behind. */}
                    {tickets.some(
                      (o, j) => j !== i && o.label.trim() !== "" && o.capacity.trim() !== "",
                    ) && (
                      <label className="block text-xs text-muted">
                        <span className="mb-1 block">
                          Only open this once another ticket is full (reserve/overflow)
                        </span>
                        <select
                          className="h-9 w-full max-w-[22rem] rounded-card border border-border bg-paper px-2 text-sm text-ink"
                          value={t.revealWhenSoldOut}
                          aria-label={`Reserve trigger for ticket ${i + 1}`}
                          onChange={(e) =>
                            setTickets((prev) =>
                              prev.map((x, j) =>
                                j === i ? { ...x, revealWhenSoldOut: e.target.value } : x,
                              ),
                            )
                          }
                        >
                          <option value="">Always available</option>
                          {tickets.map((o, j) => {
                            if (j === i || o.label.trim() === "" || o.capacity.trim() === "")
                              return null;
                            const base = (o.key.trim() || slugifyKey(o.label)).toLowerCase();
                            return (
                              <option key={j} value={base}>
                                Open once “{o.label.trim()}” is full
                              </option>
                            );
                          })}
                        </select>
                      </label>
                    )}
                  </div>
                ))}
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setTickets((prev) => [
                        ...prev,
                        { key: "", label: "", standard: "", earlyBird: "", capacity: "", note: "", revealWhenSoldOut: "" },
                      ])
                    }
                  >
                    Add another ticket
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setTickets([])}
                  >
                    Clear tickets
                  </Button>
                </div>
                <p className="text-xs text-muted">
                  A program priced by tickets can’t also use days or a family discount — the ticket
                  is the price.
                </p>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="p-cutoff">Enrolments close after (optional)</Label>
            <div className="flex items-center gap-2">
              <Input
                id="p-cutoff"
                type="date"
                className="max-w-[12rem]"
                value={cutoffDate}
                onChange={(e) => setCutoffDate(e.target.value)}
              />
              {cutoffDate.trim() !== "" && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setCutoffDate("")}
                >
                  Clear
                </Button>
              )}
            </div>
            <p className="text-xs text-muted">
              The last day parents can register. Enrolment stays open all day on this date and
              closes the next morning — the “Enrol Now” button disappears by itself, and the card
              says enrolments have closed. Leave blank to keep enrolment open for as long as the
              program is active.
            </p>
            {cutoffDate.trim() !== "" &&
              (isEnrolmentClosed({ enrol_cutoff_date: cutoffDate.trim() }) ? (
                <p className="text-xs font-semibold text-accent-fg">
                  This date has already passed — saving it closes enrolment straight away.
                </p>
              ) : (
                <p className="text-xs text-muted">
                  Open until the end of{" "}
                  <strong>{formatEventDates(cutoffDate.trim(), null)}</strong>.
                </p>
              ))}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="p-form-key">Registration form</Label>
              <Select value={formKeyChoice} onValueChange={setFormKeyChoice}>
                <SelectTrigger id="p-form-key">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FORM_KEY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                  <SelectItem value={FORM_KEY_OTHER}>Other…</SelectItem>
                </SelectContent>
              </Select>
              {formKeyChoice === FORM_KEY_OTHER && (
                <Input
                  aria-label="Custom form key"
                  value={customFormKey}
                  onChange={(e) => setCustomFormKey(e.target.value)}
                  placeholder="form key, e.g. holiday"
                  pattern="[a-z0-9_-]+"
                  title="Lowercase letters, numbers, dashes and underscores"
                />
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="p-ref-prefix">Reference prefix</Label>
              <Input
                id="p-ref-prefix"
                value={refPrefix}
                onChange={(e) => setRefPrefix(e.target.value.toUpperCase())}
                placeholder="ALC"
                maxLength={6}
                className="uppercase"
              />
            </div>
          </div>
          <p className="-mt-2 text-xs text-muted">
            The form decides what the enrol page asks (year level for term classes; date of birth
            and medical questions for a holiday program). The prefix (2–6 letters/numbers) shapes
            payment references, e.g. <strong>HOL</strong> → HOL-7F3K.
          </p>

          <div className="space-y-1.5">
            <Label>Program notes (optional)</Label>
            <RichNotesEditor initialHtml={program?.notes_html ?? ""} onChange={setNotesHtml} />
            <p className="text-xs text-muted">
              Extra details shown on this program&apos;s card on the public Programs page — what to
              bring, daily outline, links. Formatting is limited to simple text, lists and links.
            </p>
          </div>

          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
            Active (shown on the public site)
          </label>

          <label className="flex items-start gap-2 text-sm text-ink">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary"
              checked={enrollable}
              onChange={(e) => setEnrollable(e.target.checked)}
            />
            <span>
              Open for enrolment (shows the “Enrol Now” button)
              <span className="mt-0.5 block text-xs text-muted">
                Untick for an information-only listing — no button and no enrolment form.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2 text-sm text-ink">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary"
              checked={listed}
              onChange={(e) => setListed(e.target.checked)}
            />
            <span>
              List it on the Programs page
              <span className="mt-0.5 block text-xs text-muted">
                Untick for a LINK-ONLY sign-up — it still works for anyone you send the
                link to, it just isn’t advertised anywhere. Use this for a leaders form.
              </span>
            </span>
          </label>
          </div>

          {/* FEE WAIVER — only meaningful for a sign-up that sits alongside
              another program (a leaders form beside the club it serves), so it
              is offered as a plain "who gets in free" question rather than
              buried in the pricing section. */}
          <div className="space-y-1.5">
            <Label htmlFor="p-free-if">Free for people already registered in</Label>
            <Select
              value={freeIfEnrolledIn === "" ? NO_WAIVER_VALUE : freeIfEnrolledIn}
              onValueChange={(v) => setFreeIfEnrolledIn(v === NO_WAIVER_VALUE ? "" : v)}
            >
              <SelectTrigger id="p-free-if">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_WAIVER_VALUE}>Nobody — everyone pays</SelectItem>
                {allPrograms
                  .filter((p) => p.id !== program?.id)
                  .map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.title}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted">
              Anyone whose own registration is on that program pays nothing here — a parent
              with a child at the club volunteers for free, checked automatically against
              the email they sign in with. A leader who says they are under 18 always pays:
              they are usually signed up from a parent’s account, so the waiver would
              otherwise cover the whole family’s teenagers too.
            </p>
          </div>

          {(localError || error) && (
            <p className="text-sm text-accent-fg" role="alert">
              {localError ?? error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? "Saving…" : isEdit ? "Save changes" : "Add program"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Minimal WYSIWYG for the program-notes HTML — a contentEditable div plus a
 * tiny toolbar. Uses document.execCommand (deprecated but universally
 * supported), deliberately avoiding an editor dependency at this scale; the
 * Worker sanitises the saved HTML to a small allow-list either way.
 */
function RichNotesEditor({
  initialHtml,
  onChange,
}: {
  initialHtml: string;
  onChange: (html: string) => void;
}) {
  const ref = React.useRef<HTMLDivElement | null>(null);

  function exec(command: string, value?: string) {
    ref.current?.focus();
    document.execCommand(command, false, value);
    onChange(ref.current?.innerHTML ?? "");
  }

  const tools: Array<{ label: string; title: string; onClick: () => void }> = [
    { label: "B", title: "Bold", onClick: () => exec("bold") },
    { label: "I", title: "Italic", onClick: () => exec("italic") },
    { label: "• List", title: "Bullet list", onClick: () => exec("insertUnorderedList") },
    { label: "1. List", title: "Numbered list", onClick: () => exec("insertOrderedList") },
    {
      label: "Link",
      title: "Insert link",
      onClick: () => {
        const url = prompt("Link URL (https://… or mailto:…)");
        if (url && url.trim() !== "") exec("createLink", url.trim());
      },
    },
  ];

  return (
    <div className="rounded-card border border-border">
      <div className="flex flex-wrap gap-1 border-b border-border bg-paper px-2 py-1.5">
        {tools.map((t) => (
          <button
            key={t.label}
            type="button"
            title={t.title}
            /* onMouseDown + preventDefault keeps the text selection alive. */
            onMouseDown={(e) => {
              e.preventDefault();
              t.onClick();
            }}
            className="rounded px-2 py-0.5 text-xs font-semibold text-muted hover:bg-card hover:text-ink"
          >
            {t.label}
          </button>
        ))}
      </div>
      <div
        ref={ref}
        contentEditable
        role="textbox"
        aria-label="Program notes"
        aria-multiline="true"
        suppressContentEditableWarning
        className="min-h-[96px] px-3 py-2 text-sm text-ink outline-none focus:ring-2 focus:ring-primary/40 [&_a]:text-primary [&_a]:underline [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5"
        onInput={() => onChange(ref.current?.innerHTML ?? "")}
        dangerouslySetInnerHTML={{ __html: initialHtml }}
      />
    </div>
  );
}
